import { Search } from 'lucide-react';
import { useState } from 'react';
import { FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../design-system/form/form-primitives';
import { FaIcon } from '../../../../design-system/icon/fa-icon';
import { Inline } from '../../../../design-system/inline/inline';
import { Input } from '../../../../design-system/input/input';
import { Select } from '../../../../design-system/select/select';
import { SelectGroup, SelectLabel } from '../../../../design-system/select/select-primitives';
import { Stack } from '../../../../design-system/stack/stack';
import { CategoryView } from '../../../../types/entity-types';
import { cn } from '../../../../utils/cn';
import { getGroupedCategories } from '../../../../utils/get-grouped-categories';
import { CategorySelectTriggerContent } from './category-select-trigger-content';

type CategorySelectFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  inputName: TName;
  vendorCategoryId?: string;
  categories: CategoryView[];
};

export const CategorySelectField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  inputName,
  vendorCategoryId,
  categories,
}: CategorySelectFieldProps<TFieldValues, TName>) => {
  const { control } = useFormContext();

  const [search, setSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(vendorCategoryId);

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);

  const groupedCategories = getGroupedCategories({ categories });

  const filteredCategories = groupedCategories
    .map((category) => ({
      ...category,
      children: category.children.filter(
        (child) =>
          // Include the child if it matches the search OR if it's the selected category
          child.name.toLowerCase().includes(search.trim().toLowerCase()) || child.id === selectedCategoryId,
      ),
    }))
    .filter((category) => category.children.length > 0);

  return (
    <FormField
      control={control}
      name={inputName}
      render={({ field, formState }) => {
        return (
          <Stack space={1} align="stretch">
            <FormItem onBlur={field.onBlur}>
              <FormControl>
                <Select
                  aria-label="Category"
                  required
                  placeholder="Select a category"
                  value={selectedCategory?.id}
                  onValueChange={(value) => {
                    field.onChange(value);

                    setSelectedCategoryId(value);
                  }}
                  triggerLabel={<FormLabel className="text-xs font-medium text-seapoint-gray">Category</FormLabel>}
                  triggerClassName={cn(
                    'px-4 py-3 rounded-lg [&_.account-logo]:w-4 [&_.account-logo]:h-4 [&_.account-type]:hidden bg-white border border-seapoint-gray-border text-black text-base font-medium',
                    { 'border-seapoint-red': !!formState.errors.country },
                  )}
                  selectedTriggerContent={
                    <CategorySelectTriggerContent
                      level1={selectedCategory?.level1}
                      level2={selectedCategory?.level2}
                      icon={selectedCategory?.icon}
                    />
                  }
                  searchBoxConfig={{
                    visible: true,
                    searchBox: (
                      <div className="w-full p-4">
                        <Input
                          value={search}
                          leftIcon={<Search />}
                          placeholder="Search category"
                          onChange={(e) => setSearch(e.target.value)}
                          clearable
                          onClear={() => setSearch('')}
                          // Prevents the Select capturing the keyboard event when the user is typing in the search box
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      </div>
                    ),
                  }}
                >
                  <Stack className="bg-white">
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map((category) => (
                        <SelectGroup key={category.name} className="w-full">
                          <SelectLabel>{category.name}</SelectLabel>

                          {category.children.map((child) => (
                            <Select.Item key={child.id} value={child.id}>
                              <Inline space={2} wrap="nowrap">
                                <FaIcon iconName={child.icon} />

                                <p className="text-sm font-medium text-black">{child.name}</p>
                              </Inline>
                            </Select.Item>
                          ))}
                        </SelectGroup>
                      ))
                    ) : (
                      <p className="p-4 text-sm font-medium text-seapoint-gray">No categories found</p>
                    )}
                  </Stack>
                </Select>
              </FormControl>
            </FormItem>

            <FormMessage className="w-full text-right" />
          </Stack>
        );
      }}
    />
  );
};
